import { useForm } from '@magicdoorinc/form';
import { toast, useLocalization } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal, onMount } from 'solid-js';
import { useBankAccountsList } from '~/contexts/bank-accounts';
import { useCompanyBusinessInfo } from '~/contexts/company-business-info/BusinessInfoContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { mode, PaymentMode } from '~/hooks/usePaymentMode';
import { BankAccountRepository } from '~/repositories/bankAccountRepository';
import { BankAccountConnectType, PaymentAccountProvider } from '~/swagger/portal/Api';
import type { UpdateBankAccountDto } from '~/swagger/portal/Api';
export type PaymentMethod = PaymentAccountProvider.Stripe | PaymentAccountProvider.Plaid;

export enum PaymentSetupStep {
  SelectMethod = 0,
  FillBankInfo = 1,
  ConnectPayment = 2,
}

export const [PaymentSetupProvider, usePaymentSetup] = createMagicDoorContext('PaymentSetup', () => {
  const { t } = useLocalization();
  const { companyBusinessInfoListActions } = useCompanyBusinessInfo();
  const form = useForm();
  const bankAccountsList = useBankAccountsList();
  const bankAccountRepository = new BankAccountRepository();

  const [currentStep, setCurrentStep] = createSignal<PaymentSetupStep>(PaymentSetupStep.SelectMethod);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = createSignal<PaymentMethod>(PaymentAccountProvider.Stripe);
  const [syncedBankAccountId, setSyncedBankAccountId] = createSignal<string>();
  const bankAccount = createMemo(() => bankAccountsList.bankAccounts()?.find((account) => account.companyAccount === false));

  onMount(() => {
    void companyBusinessInfoListActions.fetch();
  });

  createEffect(() => {
    const account = bankAccount();
    if (account && account.id !== syncedBankAccountId()) {
      form.setFormStore({
        name: account.name,
        description: account.description,
        companyAccount: account.companyAccount,
        ...(mode() === PaymentMode.Pay ? {} : { scheduledSweepDays: account.scheduledSweepDays }),
      });
      setSyncedBankAccountId(account.id);
    }
  });

  const isBankAccountConfigured = () => {
    const account = bankAccount();
    return !!(account?.accountNumber && account?.routingNumber);
  };

  const handleSelectPaymentMethod = (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);

    if (isBankAccountConfigured()) {
      setCurrentStep(PaymentSetupStep.ConnectPayment);
    } else {
      setCurrentStep(PaymentSetupStep.FillBankInfo);
    }
  };

  const handleUpdateBankAccount = async () => {
    if (!bankAccount()?.id) throw new Error(t('Bank account not found'));

    const payload = {
      ...form.formStore,
      scheduledSweepDays: mode() === PaymentMode.Pay ? [] : (form.formStore as UpdateBankAccountDto).scheduledSweepDays,
    };

    const updated = await bankAccountRepository.updateBankAccount(bankAccount()!.id, payload as UpdateBankAccountDto);
    bankAccountsList.mutateBankAccounts((prev) => prev?.map((account) => (account.id === updated.id ? updated : account)) ?? [updated]);
    toast.success(t('Bank account updated successfully'));
    setCurrentStep(PaymentSetupStep.ConnectPayment);
  };

  const handleConnectPlaid = async () => {
    if (!bankAccount()?.id) throw new Error(t('Bank account not found'));

    const { statementUploadSessionIds, companyBusinessInfoId } = form.formStore;

    await bankAccountRepository.bankAccountConnections({
      bankAccountId: bankAccount()!.id,
      type: BankAccountConnectType.Plaid,
      statementUploadSessionIds,
      companyBusinessInfoId,
    });

    toast.success(t('Plaid connected successfully'));

    void Promise.resolve(bankAccountsList.refetchBankAccounts()).catch((error: unknown) => {
      console.error('Failed to refresh bank accounts after Plaid connection', error);
    });
  };

  const reset = () => {
    setCurrentStep(PaymentSetupStep.SelectMethod);
    setSelectedPaymentMethod(PaymentAccountProvider.Stripe);
    setSyncedBankAccountId(undefined);
    form.resetFields();
  };

  const goBack = () => {
    setCurrentStep(PaymentSetupStep.SelectMethod);
  };

  return {
    form,
    currentStep,
    selectedPaymentMethod,
    bankAccount,
    handleSelectPaymentMethod,
    handleUpdateBankAccount,
    handleConnectPlaid,
    reset,
    goBack,
  };
});
